<script setup lang="ts">
import { computed, ref } from "vue";
import type { ComponentExposed } from "vue-component-type-helpers";

import { backendOld } from "~/api";
import Button from "~/components/dumb/Button.vue";
import ConfirmationDialog from "~/components/dumb/ConfirmationDialog.vue";
import SimpleTable from "~/components/dumb/SimpleTable.vue";
import UpgradeConfirmationDialog from "~/components/dumb/UpgradeConfirmationDialog.vue";
import SettingsCopyText from "~/components/settings/SettingsCopyText.vue";
import WebhookModal from "~/components/WebhookModal.vue";
import { EditIcon, TrashIcon } from "~/icons";
import {
  ButtonStyle,
  DialogMode,
  DropdownMenuItemKind,
  Entitlement,
  Subscription,
  WebhookCellType,
} from "~/shared/enums";
import type { Webhook } from "~/shared/types";
import { useAppStore, useDataStore, usePageStore, useTenantStore } from "~/stores";
import WebhookCell from "~/views/settings/WebhookCell.vue";

const ROTATE_SECRET_DIALOG_DESCRIPTION =
  "Rotating the webhook secret will invalidate the old secret and make a new one. This can't be undone. Are you sure you want to proceed?";

const DELETE_WEBHOOK_DIALOG_DESCRIPTION =
  "Permanently deleting a webhook can't be undone. Are you sure you want to proceed?";

const appStore = useAppStore();
const dataStore = useDataStore();
const pageStore = usePageStore();
const tenantStore = useTenantStore();

const table = ref<ComponentExposed<typeof SimpleTable<Webhook>> | null>(null);
const deleteWebhookModal = ref<InstanceType<typeof ConfirmationDialog> | null>(null);

const confirmationDialog = ref<InstanceType<typeof UpgradeConfirmationDialog> | null>(null);

const webhookToRemove = ref<Webhook | null>(null);

const upgradeRequired = computed(() => !tenantStore.getEntitlementValue(Entitlement.WEBHOOKS));

const webhookInWebhookModal = ref<Webhook | undefined>(undefined);
const webhookModalOpen = ref(false);
const webhooks = computed<Webhook[]>(() => dataStore.webhookList);

const rotateSecret = async () => {
  const { data } = await backendOld.workspace.rotateWebhookSecret();
  tenantStore.webhookSecret = data.webhookSecret;
};

const deleteWebhook = () => {
  if (!webhookToRemove.value) {
    return;
  }

  dataStore.deleteWebhook(webhookToRemove.value);
};

const openWebhookModal = (webhook?: Webhook) => {
  webhookInWebhookModal.value = webhook ? { ...webhook } : undefined;
  webhookModalOpen.value = true;
};
const closeWebhookModal = () => {
  webhookInWebhookModal.value = undefined;
  webhookModalOpen.value = false;
};

const addEndpoint = () => {
  if (upgradeRequired.value) {
    confirmationDialog.value?.openModal();
    return;
  }
  openWebhookModal();
};

const getContextMenuSections = (webhook: Webhook) => [
  {
    title: "Meta",
    items: [
      {
        title: "Edit",
        kind: DropdownMenuItemKind.BUTTON,
        icon: EditIcon,
        onClick: () => {
          openWebhookModal({ ...webhook });
        },
      },
      {
        title: "Delete",
        kind: DropdownMenuItemKind.BUTTON,
        icon: TrashIcon,
        onClick: () => {
          webhookToRemove.value = webhook;
          deleteWebhookModal.value?.openModal();
        },
      },
    ],
  },
];

const onContextMenu = (event: MouseEvent, webhook: Webhook) => {
  if (tenantStore.isDart && !pageStore.adminHidden && event.altKey) {
    return;
  }
  appStore.openContextMenu(event as PointerEvent, getContextMenuSections(webhook));
};

const columns = computed(() => [
  {
    title: "Webhook URL",
    hideTitle: true,
    cell: WebhookCell,
    getCellArgs: () => ({ cellType: WebhookCellType.URL }),
    classes: "w-full max-w-0 pr-2",
  },
  {
    title: "Events",
    cell: WebhookCell,
    getCellArgs: () => ({ cellType: WebhookCellType.EVENTS }),
    classes: "pr-2",
    headerClasses: "min-w-28 text-left",
  },
  {
    title: "Status",
    cell: WebhookCell,
    getCellArgs: () => ({ cellType: WebhookCellType.STATUS }),
    classes: "w-28",
    headerClasses: "min-w-28 text-left",
  },
  {
    title: "Actions",
    hideTitle: true,
    cell: WebhookCell,
    getCellArgs: (webhook: Webhook) => ({
      cellType: WebhookCellType.ACTIONS,
      getContextMenuSections: () => getContextMenuSections(webhook),
    }),
    classes: "w-6",
    headerClasses: "w-6",
  },
]);
</script>

<template>
  <div class="flex flex-col">
    <div class="mb-10 flex flex-col justify-center space-y-3">
      <div class="flex items-center gap-4">
        <h2 class="select-none text-xl text-md">Webhooks</h2>
        <UpgradeConfirmationDialog
          v-if="upgradeRequired"
          ref="confirmationDialog"
          feature-action="use webhooks"
          :subscription="Subscription.PREMIUM"
          no-button />
      </div>
      <div class="select-none text-sm/relaxed text-lt">
        <p>
          Dart can be configured to send updates to an endpoint of yours to automate some common workflows and build
          other custom integrations or processes.
        </p>
        <br />
        <p>
          Find the documentation for the API in the
          <a href="/api/v0/public/docs/" target="_blank" rel="noopener noreferrer">
            <span class="underline text-vlt">OpenAPI spec</span>
          </a>
          documentation or in one of the client libraries.
        </p>
        <br />
        <p>
          Read
          <a href="https://help.dartai.com/articles/9024895-webhooks" target="_blank" rel="noopener noreferrer">
            <span class="underline text-vlt">this help center article</span>
          </a>
          for more information on how to get this configured.
        </p>
      </div>
    </div>
    <div class="mb-6 flex select-none flex-col gap-1">
      <div class="text-md">Webhook secret</div>
      <SettingsCopyText
        :text="tenantStore.webhookSecret"
        label="Webhook secret"
        :rotate-dialog-description="ROTATE_SECRET_DIALOG_DESCRIPTION"
        @rotate-or-create="rotateSecret" />
    </div>

    <div class="my-4 flex items-center justify-between">
      <span class="text-base font-medium text-md">Webhook endpoints</span>
      <Button :btn-style="ButtonStyle.PRIMARY" text="Add endpoint" @click="addEndpoint" />
    </div>

    <WebhookModal v-if="webhookModalOpen" :webhook="webhookInWebhookModal" @close="closeWebhookModal" />
    <template v-if="webhooks.length > 0 && !upgradeRequired">
      <ConfirmationDialog
        ref="deleteWebhookModal"
        :mode="DialogMode.DELETE"
        title="Delete webhook"
        :description="DELETE_WEBHOOK_DIALOG_DESCRIPTION"
        confirm-text="Delete"
        cancel-text="Cancel"
        :icon="TrashIcon"
        @confirm="deleteWebhook" />
      <SimpleTable ref="table" :columns="columns" :rows="webhooks" @context-menu="onContextMenu" />
    </template>
  </div>
</template>
